/**
 * CropOverlay — overlay for drawing bounding box on product image
 */

import { createElement } from '../utils/dom.js';
import { EVENTS } from '../core/events.js';
import { toRelativeBbox, normalizeBbox } from '../utils/coordinates.js';

export default class CropOverlay {
  /**
   * @param {Object} eventBus - EventBus instance
   * @param {Object} i18n - I18n instance
   */
  constructor(eventBus, i18n) {
    this._eventBus = eventBus;
    this._i18n = i18n;

    this._el = null;
    this._canvas = null;
    this._ctx = null;
    this._img = null;
    this._searchBtn = null;

    this._imageUrl = null;
    this._genderId = null;
    this._drawing = false;
    this._startX = 0;
    this._startY = 0;
    this._currentX = 0;
    this._currentY = 0;
    this._hasBbox = false;

    // Bound handlers
    this._onMouseDown = this._handleMouseDown.bind(this);
    this._onMouseMove = this._handleMouseMove.bind(this);
    this._onMouseUp = this._handleMouseUp.bind(this);
    this._onTouchStart = this._handleTouchStart.bind(this);
    this._onTouchMove = this._handleTouchMove.bind(this);
    this._onTouchEnd = this._handleTouchEnd.bind(this);
  }

  /**
   * Open the crop overlay with an image
   * @param {string} imageUrl
   * @param {number|null} genderId
   */
  open(imageUrl, genderId) {
    this._imageUrl = imageUrl;
    this._genderId = genderId;
    this._hasBbox = false;
    this._render();
  }

  /**
   * Close the crop overlay
   */
  close() {
    this._cleanup();
    this._eventBus.emit(EVENTS.CROP_CANCEL);
  }

  /** @private */
  _render() {
    this._cleanup();

    this._el = createElement('div', 'grb-widget--crop-overlay');

    const container = createElement('div', 'grb-widget--crop-container');

    // Image
    this._img = createElement('img', 'grb-widget--crop-image', {
      src: this._imageUrl,
    });

    // Canvas for drawing
    this._canvas = createElement('canvas', 'grb-widget--crop-canvas');
    this._ctx = this._canvas.getContext('2d');

    // Wait for image to load to set canvas size
    this._img.addEventListener('load', () => {
      this._canvas.width = this._img.clientWidth;
      this._canvas.height = this._img.clientHeight;
    });

    container.appendChild(this._img);
    container.appendChild(this._canvas);

    // Hint text
    const hint = createElement('div', 'grb-widget--crop-hint', {}, [this._i18n.t('crop.hint')]);

    // Buttons
    const buttons = createElement('div', 'grb-widget--crop-buttons');

    this._searchBtn = createElement('button', 'grb-widget--crop-search-btn', {
      type: 'button',
      disabled: 'disabled',
    }, [this._i18n.t('crop.search')]);

    const cancelBtn = createElement('button', 'grb-widget--crop-cancel-btn', {
      type: 'button',
    }, [this._i18n.t('crop.cancel')]);

    this._searchBtn.addEventListener('click', () => this._onSearch());
    cancelBtn.addEventListener('click', () => this.close());

    buttons.appendChild(this._searchBtn);
    buttons.appendChild(cancelBtn);

    this._el.appendChild(container);
    this._el.appendChild(hint);
    this._el.appendChild(buttons);

    document.body.appendChild(this._el);

    // Attach drawing handlers
    this._canvas.addEventListener('mousedown', this._onMouseDown);
    this._canvas.addEventListener('mousemove', this._onMouseMove);
    this._canvas.addEventListener('mouseup', this._onMouseUp);
    this._canvas.addEventListener('touchstart', this._onTouchStart, { passive: false });
    this._canvas.addEventListener('touchmove', this._onTouchMove, { passive: false });
    this._canvas.addEventListener('touchend', this._onTouchEnd);
  }

  /** @private */
  _handleMouseDown(e) {
    const rect = this._canvas.getBoundingClientRect();
    this._startX = e.clientX - rect.left;
    this._startY = e.clientY - rect.top;
    this._drawing = true;
    this._hasBbox = false;
    this._searchBtn.disabled = true;
  }

  /** @private */
  _handleMouseMove(e) {
    if (!this._drawing) return;
    const rect = this._canvas.getBoundingClientRect();
    this._currentX = e.clientX - rect.left;
    this._currentY = e.clientY - rect.top;
    this._drawRect();
  }

  /** @private */
  _handleMouseUp() {
    if (!this._drawing) return;
    this._drawing = false;
    this._finalizeBbox();
  }

  /** @private */
  _handleTouchStart(e) {
    e.preventDefault();
    const touch = e.touches[0];
    const rect = this._canvas.getBoundingClientRect();
    this._startX = touch.clientX - rect.left;
    this._startY = touch.clientY - rect.top;
    this._drawing = true;
    this._hasBbox = false;
    this._searchBtn.disabled = true;
  }

  /** @private */
  _handleTouchMove(e) {
    e.preventDefault();
    if (!this._drawing) return;
    const touch = e.touches[0];
    const rect = this._canvas.getBoundingClientRect();
    this._currentX = touch.clientX - rect.left;
    this._currentY = touch.clientY - rect.top;
    this._drawRect();
  }

  /** @private */
  _handleTouchEnd() {
    if (!this._drawing) return;
    this._drawing = false;
    this._finalizeBbox();
  }

  /** @private Draw selection rectangle with dimming outside */
  _drawRect() {
    const w = this._canvas.width;
    const h = this._canvas.height;
    this._ctx.clearRect(0, 0, w, h);

    // Dim the entire image
    this._ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    this._ctx.fillRect(0, 0, w, h);

    // Clear the selected area (show image through)
    const [x1, y1, x2, y2] = normalizeBbox(this._startX, this._startY, this._currentX, this._currentY);
    this._ctx.clearRect(x1, y1, x2 - x1, y2 - y1);

    // Draw border around selection
    this._ctx.strokeStyle = '#fff';
    this._ctx.lineWidth = 2;
    this._ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
  }

  /** @private */
  _finalizeBbox() {
    const bw = Math.abs(this._currentX - this._startX);
    const bh = Math.abs(this._currentY - this._startY);

    // Require minimum size
    if (bw > 10 && bh > 10) {
      this._hasBbox = true;
      this._searchBtn.disabled = false;
    }
  }

  /** @private */
  _onSearch() {
    if (!this._hasBbox) return;

    const [x1, y1, x2, y2] = normalizeBbox(this._startX, this._startY, this._currentX, this._currentY);
    const bbox = toRelativeBbox(x1, y1, x2, y2, this._canvas.width, this._canvas.height);

    this._eventBus.emit(EVENTS.CROP_COMPLETE, {
      imageUrl: this._imageUrl,
      bbox,
      genderId: this._genderId,
    });

    this._cleanup();
  }

  /** @private Remove overlay DOM and listeners */
  _cleanup() {
    if (this._canvas) {
      this._canvas.removeEventListener('mousedown', this._onMouseDown);
      this._canvas.removeEventListener('mousemove', this._onMouseMove);
      this._canvas.removeEventListener('mouseup', this._onMouseUp);
      this._canvas.removeEventListener('touchstart', this._onTouchStart);
      this._canvas.removeEventListener('touchmove', this._onTouchMove);
      this._canvas.removeEventListener('touchend', this._onTouchEnd);
    }

    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
    }

    this._el = null;
    this._canvas = null;
    this._ctx = null;
    this._img = null;
  }

  destroy() {
    this._cleanup();
  }
}
